import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { LoaderSymbol } from '../../components/loading';
import { ParametersTab } from './sampleRegistration/SampleLoginView';
import { SampleTransferContainers } from './SampleTransferContainers';
import {
  mapLookupRows, mapSampleLoginsPayload,
  type SampleLoginsDto, type SampleRegistrationLookupRowDto,
} from './sampleRegistration/SampleRegistrationDetailPage';
import type { SrSample } from './sampleRegistration/srData';

/**
 * Sample Transfer → the two tabs that are Sample Registration's, rendered as
 * Registration's OWN components rather than re-implemented (standing rule 8, so
 * the two screens cannot drift):
 *
 *  · **Containers** — All in One (default) / By Sample, editable bottle columns,
 *    the bottle-letter picker and Copy / Reset Bottles.
 *  · **Parameters** — every analyte for the job, with the field-method Result /
 *    Units / AnalysisDate / AnalyzedBy cells editable.
 *
 * Both are pure functions of the same in-memory `SrSample[]` model, so they share
 * one loader here: the SAME `sample-logins` payload through the SAME
 * `mapSampleLoginsPayload` mapper Registration's own Edit-Mode load uses.
 *
 * ⚠️ EDITS ARE NOT PERSISTED FROM THIS SCREEN YET. Registration writes both
 * through its WHOLE-JOB `PUT /api/v1/sample-registration/{jobOid}`, whose body
 * also carries the registration header, the samples and the sample tests, and
 * whose children SP re-writes what it is given. Whether a transfer-time edit may
 * write back into registration at all is a product decision, so it is left for
 * its own reviewed change rather than guessed at here.
 */

/** Load the job's samples + tests + bottles into Registration's in-memory model. */
function useJobSamples(jobOid: string) {
  const notify = useNotify();
  const [samples, setSamples] = useState<SrSample[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        const res = await api.get(`/api/v1/sample-registration/${jobOid}/sample-logins`);
        if (!live) return;
        setSamples(mapSampleLoginsPayload((res.data as { data: SampleLoginsDto | null }).data));
      } catch {
        if (live) { setSamples([]); notify.error('Failed to load the job samples.'); }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [jobOid, notify]);

  return { samples, setSamples, loading };
}

/** The three catalogs the Parameters tab resolves its cells against. All are
 *  OPTIONAL on the component, so a failure here degrades to raw stored values
 *  rather than breaking the tab. */
function useRegLookups(jobOid: string) {
  const [lookups, setLookups] = useState<ReturnType<typeof mapLookupRows> | null>(null);
  const [collectors, setCollectors] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await api.get('/api/v1/sample-registration/lookups');
        if (!live) return;
        const rows = (res.data as { data: SampleRegistrationLookupRowDto[] | null }).data ?? [];
        setLookups(mapLookupRows(rows));
      } catch { /* optional — the tab falls back to raw values */ }
    })();
    return () => { live = false; };
  }, []);

  // AnalyzedBy resolves through the JOB’s collectors, not the registration lookups.
  // That SP returns only the CLIENT-scoped picker list (co.CustomerName = @ClientOid),
  // so a sample carrying a collector who is not on its client’s list rendered as a raw
  // GUID - 790 jobs in live data are in that position (owner report 2026-09-28, job
  // 260925006: f5943fc1-… is Ernest Payne, absent from that client’s list).
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await api.get(`/api/v1/sample-transfer/${jobOid}/collectors`);
        if (!live) return;
        setCollectors((res.data as { data: { id: string; name: string }[] | null }).data ?? []);
      } catch { /* optional — falls back to the raw value */ }
    })();
    return () => { live = false; };
  }, [jobOid]);

  // Oid → name AND name → name, exactly as Registration builds them: a persisted
  // sample stores the FK Oid (lowercase, .NET Guid.ToString()) while a newly-added
  // one stores the display name.
  const oidNameMap = (obl: Record<string, string> | undefined) => {
    const m: Record<string, string> = {};
    for (const [name, oid] of Object.entries(obl ?? {})) {
      m[oid.toLowerCase()] = name;
      m[name] = name;
    }
    return m;
  };

  const matrixMap = useMemo(() => oidNameMap(lookups?.matrix?.oidByLabel), [lookups]);
  const collectorMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of collectors) {
      m[c.id.toLowerCase()] = c.name;   // the stored Oid (.NET Guid.ToString() is lowercase)
      m[c.name] = c.name;               // a newly-added sample may store the name
    }
    return m;
  }, [collectors]);
  const unitOptions = useMemo(
    () => (lookups?.unit?.rows ?? []).map(r => r[0]).filter((v): v is string => !!v),
    [lookups]);

  return { matrixMap, collectorMap, unitOptions };
}

function Busy() {
  return (
    <div className="stx-pop-body" style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
      <LoaderSymbol />
    </div>
  );
}

/** Sample Transfer ▸ Containers — Sample Transfer's OWN bottle-aware view (one row per
 *  bottle, receipt-safe per-bottle save), NOT Registration's per-test ContainersTab which
 *  collapsed two bottles of one test into a single row (owner request 2026-09-29). */
export function SampleTransferContainersTab({ jobOid, jobId }: { jobOid: string; jobId: string }) {
  return <SampleTransferContainers jobOid={jobOid} jobId={jobId} />;
}

/** Sample Transfer ▸ Parameters — Registration's ParametersTab. */
export function SampleTransferParametersTab({ jobOid }: { jobOid: string }) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { samples, setSamples, loading } = useJobSamples(jobOid);
  const { matrixMap, collectorMap, unitOptions } = useRegLookups(jobOid);

  const onSuccess = useCallback((m: string) => notify.success(m), [notify]);

  // Registration hands the tab an imperative confirm(title, message, run, danger);
  // route it through the ONE shared dialog (standing rule 7) rather than a second.
  const confirmAction = useCallback(
    (title: string, message: string, run: () => void, danger?: boolean) => {
      void (async () => {
        if (await confirm({ title, message, tone: danger ? 'danger' : undefined })) run();
      })();
    }, [confirm]);

  if (loading) return <Busy />;
  return (
    <div className="stx-pop-body">
      {/* Read-only in Sample Transfer — legacy has no editable/Delete Parameters view
          (results are entered in Field Data Entry); Export is the only action here
          (owner report 2026-09-30). */}
      <ParametersTab
        samples={samples} setSamples={setSamples}
        onSuccess={onSuccess} confirmAction={confirmAction}
        matrixMap={matrixMap} collectorMap={collectorMap} unitOptions={unitOptions} readOnly />
    </div>
  );
}
